import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {ServerErrorUtils} from '../../utils/server-error-utils';
import {SnackBarService} from '../../../core/services/snackbar.service';
import {debounceTime, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import { SortUtils } from '../../utils/sort-utils';
import { Advertiser } from '../../domain/campaigns.model';
import { CampaignsService } from '../../services/campaigns.service';
import { ActiveInactiveArchivedColorMap } from '../../enums/active-inactive-archived.enum';

@Component({
    selector: 'ctv-advertiser-autocomplete-chips-input',
    template: `
    <mat-form-field [ngClass]="formFieldClass ? formFieldClass : 'w-100'" [floatLabel]="floatLabel || 'always'" [appearance]="formFieldAppearance ? formFieldAppearance : undefined">
      <mat-label>{{label ? label : 'Chose advertiser'}}</mat-label>
      <mat-chip-grid #chipList [disabled]='loading' class="form-field-chip-list">
        @for (advertiser of selectedAdvertisers; track advertiser) {
          <mat-basic-chip
            [hidden]="classicChips"
            [removable]='checkIfRemovable(advertiser.id)'
            (removed)='remove(advertiser)'>
            <i class="fas fa-circle status-circle small mr-2" ngClass="text-{{ActiveInactiveArchivedColorMap[advertiser.status]}}"></i>
            {{advertiser?.name}}
            @if (checkIfRemovable(advertiser.id)) {
              <button type="button" class="chip-remove-btn" matChipRemove aria-label="Remove"><i class="fas fa-times-circle text-danger" aria-hidden="true"></i></button>
            }
          </mat-basic-chip>
        }
        <input
          matInput
          #search
          aria-label="Advertiser"
          [matAutocomplete]="auto"
          [formControl]="filterCtrl"
          [placeholder]="placeholder ? placeholder : 'Search'"
          [required]="required"
          [matChipInputFor]='chipList'
          />
      </mat-chip-grid>
      <i class="far fa-spin fa-spinner text-primary" matSuffix [hidden]="!loading"></i>
      <mat-autocomplete #auto='matAutocomplete' panelClass="ctv-multi-autocomplete-panel" (optionSelected)='optionSelected($event)'>
        @for (advertiser of filteredItems | async; track advertiser) {
          <mat-option [value]='advertiser'>
            <mat-checkbox class="ctv-multi-option-check" color="primary" [checked]="isAdvertiserSelected(advertiser)" [disableRipple]="true" tabindex="-1"></mat-checkbox>
            <i class="fas fa-circle status-circle small" ngClass="text-{{ActiveInactiveArchivedColorMap[advertiser.status]}}"></i>
            {{advertiser.name}}
          </mat-option>
        }
      </mat-autocomplete>
      <!--<mat-hint>Start typing (autocomplete)</mat-hint>-->
    </mat-form-field>
    
    <div class="filter-chips" [hidden]="!classicChips || selectedAdvertisers?.length === 0">
      @for (advertiser of selectedAdvertisers; track advertiser) {
        <div class="filter-chips__chip">
          <span class="filter-chips__name">{{advertiser.name}}</span>
          <button type="button" class="filter-chips__remove" (click)="remove(advertiser)" [attr.aria-label]="'Remove ' + advertiser.name">
            <i class="fas fa-times-circle text-danger" aria-hidden="true"></i>
          </button>
        </div>
      }
    </div>`,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AdvertiserAutocompleteChipsInputComponent implements OnInit, OnDestroy {
  @ViewChild('search', { static: true }) searchFilter: ElementRef;
  private readonly onDestroy = new Subject<void>();

  @Input() placeholder: string;
  @Input() floatLabel: string;
  @Input() label: string;
  @Input() formFieldClass: string;
  @Input() formFieldAppearance: string;
  @Input() required: boolean;
  /** If false, initial/preselected advertisers will not be removable. Default is true.*/
  @Input() initialAdvertisersRemovable = true;
  @Input() classicChips: boolean;
  @Input() preselectedAdvertiserIds: Advertiser['id'][];
  @Output() advertiserSelectionChange = new EventEmitter<Advertiser[]>();

  filteredItems: Subject<Advertiser[]>; //List of Advertisers to show in dropdown filtered with 'filteredAdvertiserIds'
  filterCtrl = new UntypedFormControl();
  advertiserList: Advertiser[] = []; //stores currently fetched list of advertisers
  selectedAdvertisers: Advertiser[] = []; //currently selected advertisers
  filteredAdvertiserIds: string[] = []; //It stores all the Id's of selected advertisers
  initialAdvertiserIds: string[] = []; //stores initial advertiser Id's
  public loading = false;
  public readonly ActiveInactiveArchivedColorMap = ActiveInactiveArchivedColorMap;

  constructor(
    private service: CampaignsService,
    private snackBarService: SnackBarService) {
    this.filteredItems = new Subject();
    this.filterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(
        (searchTerm) => {
          if (searchTerm && typeof searchTerm === 'string') {
            this.getAdvertisers(searchTerm);
          }
        }
      );
  }

  ngOnInit(): void {
    if (this.preselectedAdvertiserIds && this.preselectedAdvertiserIds?.length) {
      this.getAdvertisers('', this.preselectedAdvertiserIds);
    }
    if (!this.initialAdvertisersRemovable) {
      this.initialAdvertiserIds = this.preselectedAdvertiserIds;
    }
  }

  /** SearchTerm used for filtering by advertiser name. preselectedAdvertiserIds used to get all preselected advertisers at the component initialization. */
  private getAdvertisers(searchTerm?: string, preselectedAdvertiserIds?: string[]) {
    this.loading = true;
    this.filterCtrl.disable();
    return this.service.getAdvertiserList({
      page: 0,
      size: 1000,
      name: searchTerm,
      ids: preselectedAdvertiserIds
    } as any)
      .pipe(
        finalize(() => {
          this.filterCtrl.enable();
          !preselectedAdvertiserIds ? this.searchFilter.nativeElement.focus() : 0; //No need for focus when loading page with preselected advertiser
          this.loading = false;
        }),
        takeUntil(this.onDestroy)
      )
      .subscribe(
        (resp) => {
          this.advertiserList = resp.content.sort(SortUtils.propertyComparatorString('name'));
          if (preselectedAdvertiserIds) {
            this.selectedAdvertisers = this.advertiserList;
            this.updateFilteredAdvertisers(this.selectedAdvertisers);
            this.emit();
          }
          this.filteredItems.next(this.advertiserList.filter(x => !this.filteredAdvertiserIds.includes(x.id)));
        },
        (error) => {
          const messages = ServerErrorUtils.getValidationMessages(error);
          if (messages) {
            messages.forEach((m) => this.snackBarService.error(m));
          } else {
            this.snackBarService.error('Error while fetching advertiser autocomplete data');
          }
        },
      );
  }

  public optionSelected($event) {
    this.selectedAdvertisers.push($event.option.value);
    this.emit();
    this.filterCtrl.setValue(null);
    this.searchFilter.nativeElement.value = '';
    this.updateFilteredAdvertisers(this.selectedAdvertisers);
    this.filteredItems.next([]); //Reset list since the input value of autocomplete is empty and there is no point in showing filtered list on next focus on element
  }

  /** Emits value back to parent component. */
  private emit() {
    this.advertiserSelectionChange.emit(this.selectedAdvertisers);
  }

  public updateFilteredAdvertisers(items) {
    this.filteredAdvertiserIds = items.map((item) => item.id);
  }

  isAdvertiserSelected(advertiser: Advertiser): boolean {
    return !!this.selectedAdvertisers?.some((selected) => selected?.id === advertiser?.id);
  }

  public checkIfRemovable(id: string): boolean {
    if (this.initialAdvertiserIds?.includes(id)) {
      return false;
    }
    else {return true;}
  }

  public remove(p: Advertiser) {
    this.selectedAdvertisers.splice(this.selectedAdvertisers.indexOf(p), 1);
    this.advertiserSelectionChange.emit(this.selectedAdvertisers);
    this.updateFilteredAdvertisers(this.selectedAdvertisers);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
  }
}
